import type { ExtractionDataset } from "@/features/data-curation/types";

export type DatasetDisplayStatus =
  | "awaiting-approval"
  | "not-started"
  | "editable"
  | "collecting"
  | "completed";

export const datasetDisplayStatus = (
  dataset: ExtractionDataset,
  collecting = false,
): DatasetDisplayStatus => {
  if (dataset.status === "draft") return "awaiting-approval";
  if (collecting) return "collecting";
  if (dataset.status === "active" && dataset.collection_waiting) return "not-started";
  if (dataset.status === "finalized" || (dataset.checkpoint_count ?? 0) > 0) {
    return "completed";
  }
  return "editable";
};

export const statusVariant = (status: DatasetDisplayStatus) => {
  if (status === "collecting") return "blue" as const;
  if (status === "completed") return "green" as const;
  return "default" as const;
};

export const statusLabel = (status: DatasetDisplayStatus) => {
  if (status === "awaiting-approval") return "Awaiting approval";
  if (status === "not-started") return "Not started";
  if (status === "collecting") return "Collecting";
  if (status === "completed") return "Completed";
  return "Editable";
};

/** The state of a curation in the sidebar list. */
export type CurationListStatus =
  | "working"
  | "awaiting-approval"
  | "awaiting-start"
  | "awaiting-reply"
  | "completed"
  | "paused";

/** The state of a curation; `null` is a new curation that has no dataset yet. */
export const curationListStatus = (
  dataset: ExtractionDataset | null,
  agentWorking: boolean,
): CurationListStatus => {
  if (agentWorking) return "working";
  if (!dataset) return "awaiting-reply";
  const status = datasetDisplayStatus(dataset);
  if (status === "awaiting-approval" || status === "completed") return status;
  if (status === "not-started") return "awaiting-start";
  return "paused";
};

export const curationListStatusLabel: Record<CurationListStatus, string> = {
  working: "Agent working",
  "awaiting-approval": "Waiting for your approval",
  "awaiting-start": "Waiting for you to start",
  "awaiting-reply": "Waiting for your reply",
  completed: "Complete",
  paused: "Paused",
};
